// Library Methods
import axios from "axios";
import { HubConnection } from "@microsoft/signalr";

// Utils
import { injectBearer, webServiceUrl } from "src/utils/services/servicesHelper";

// Types
import { PortalHubType } from "./types";

const getPortalHub = async (): Promise<undefined | PortalHubType> => {
  try {
    const { data } = await axios.get(`${webServiceUrl}/live-data/portal-hub`);
    return data?.data;
  } catch (error) {
    throw error;
  }
};

const subscribeToClinic = async (
  token: string,
  clinicId: string,
  connection: HubConnection
): Promise<string> => {
  const payload = {
    token: injectBearer(token),
    clinicId,
  };
  return await connection.invoke("SubscribeToClinic", JSON.stringify(payload));
};

const subscribeToDevice = async (
  token: string,
  deviceId: string,
  connection: HubConnection
) => {
  const payload = {
    token: injectBearer(token),
    deviceId,
  };
  return await connection.invoke("SubscribeToDevice", JSON.stringify(payload));
};

const unsubscribeFromDevice = async (
  deviceId: string,
  connection: HubConnection
) => {
  return await connection.invoke("UnSubscribeFromDevice", deviceId);
};

const updateDeviceStage = async (
  token: string,
  deviceId: string,
  stage: string,
  connection: HubConnection
) => {
  const payload = {
    token: injectBearer(token),
    deviceId,
    stage,
  };
  return await connection.invoke(
    "RequestUpdateDeviceStage",
    JSON.stringify(payload)
  );
};

const skipExamRequest = async (
  token: string,
  deviceId: string,
  connection: HubConnection
) => {
  const payload = {
    token: injectBearer(token),
    deviceId,
  };
  return await connection.invoke("RequestSkipSection", JSON.stringify(payload));
};

const terminateExamRequest = async (
  token: string,
  deviceId: string,
  connection: HubConnection
) => {
  const payload = {
    token: injectBearer(token),
    deviceId,
  };
  return await connection.invoke(
    "RequestExamTermination",
    JSON.stringify(payload)
  );
};

const deviceCalibrateLobby = async (
  token: string,
  deviceId: string,
  connection: HubConnection
) => {
  const payload = {
    token: `Bearer ${token}`,
    deviceId,
  };
  return await connection.send(
    "SendMessageToDevice",
    "DeviceRecenterRequested",
    JSON.stringify(payload)
  );
};

const deviceEnableControllerBeep = async (
  token: string,
  deviceId: string,
  connection: HubConnection
) => {
  const payload = {
    token: injectBearer(token),
    deviceId,
  };
  return await connection.send(
    "SendMessageToDevice",
    "DeviceEnableVisualFieldControllerBeepRequested",
    JSON.stringify(payload)
  );
};

const deviceDisableControllerBeep = async (
  token: string,
  deviceId: string,
  connection: HubConnection
) => {
  const payload = {
    token: injectBearer(token),
    deviceId,
  };
  return await connection.send(
    "SendMessageToDevice",
    "DeviceDisableVisualFieldControllerBeepRequested",
    JSON.stringify(payload)
  );
};

const deviceEnableWarnings = async (
  token: string,
  deviceId: string,
  connection: HubConnection
) => {
  const payload = {
    token: injectBearer(token),
    deviceId,
  };
  return await connection.send(
    "SendMessageToDevice",
    "DeviceEnableVisualFieldWarningsRequested",
    JSON.stringify(payload)
  );
};

const deviceDisableWarnings = async (
  token: string,
  deviceId: string,
  connection: HubConnection
) => {
  const payload = {
    token: injectBearer(token),
    deviceId,
  };
  return await connection.send(
    "SendMessageToDevice",
    "DeviceDisableVisualFieldWarningsRequested",
    JSON.stringify(payload)
  );
};

const deviceEnableSubtitles = async (
  token: string,
  deviceId: string,
  connection: HubConnection
) => {
  const payload = {
    token: injectBearer(token),
    deviceId,
  };
  return await connection.send(
    "SendMessageToDevice",
    "DeviceEnableSubtitlesRequested",
    JSON.stringify(payload)
  );
};

const deviceDisableSubtitles = async (
  token: string,
  deviceId: string,
  connection: HubConnection
) => {
  const payload = {
    token: injectBearer(token),
    deviceId,
  };
  return await connection.send(
    "SendMessageToDevice",
    "DeviceDisableSubtitlesRequested",
    JSON.stringify(payload)
  );
};

const deviceUpdateVolume = async (
  token: string,
  deviceId: string,
  volume: string,
  connection: HubConnection
) => {
  const payload = {
    token: injectBearer(token),
    deviceId,
    volume,
  };
  return await connection.invoke(
    "RequestUpdateDeviceVolume",
    JSON.stringify(payload)
  );
};

// WebRtc methods signaling method
const invokeSendOffer = async (
  token: string,
  deviceId: string,
  offer: RTCSessionDescriptionInit | undefined,
  connection: HubConnection
) => {
  const payload = {
    token: `Bearer ${token}`,
    deviceId,
    offer,
  };
  return await connection.invoke("SendOffer", JSON.stringify(payload));
};

const invokeSendIceCandidate = async (
  token: string,
  deviceId: string,
  iceCandidate: {
    Candidate: string;
    SdpMid: string | null;
    SdpMlineIndex: number | null;
  },
  connection: HubConnection
) => {
  const payload = {
    token: `Bearer ${token}`,
    deviceId,
    iceCandidate,
  };
  return await connection.invoke("SendIceCandidate", JSON.stringify(payload));
};

const invokeNotifyAnswerAccepted = async (
  token: string,
  deviceId: string,
  connection: HubConnection
) => {
  const payload = {
    token: `Bearer ${token}`,
    deviceId,
  };
  return await connection.invoke(
    "NotifyAnswerAccepted",
    JSON.stringify(payload)
  );
};

const deviceUpdateLensParameter = async (
  token: string,
  deviceId: string,
  data: {
    lensName?: string;
    profileName?: string;

    eye?: string;

    // shared
    preferred?: boolean;
    currentlyBeingViewed?: boolean;

    // iol
    halosType?: string;
    starburstsType?: string;
    glaresType?: string;
    reducedContrastType?: string;
    toricCylinderType?: number | null;

    // progressive
    corridorWidthType?: string;
    corridorLengthType?: string;
    depthDistortType?: string;
    indexType?: string;
    isBlueLightFilterEnabled?: boolean;
    isTintEnabled?: boolean;
    tintColor?: string;
    tintDensityType?: string;

    // dry eye (also uses halosType, glaresType and reducedContrastType)
    severityType?: string;
    blurType?: string;
    tearBreakUpTimeType?: string;
    treated?: boolean;
  },
  connection: HubConnection
) => {
  const payload = {
    token: injectBearer(token),
    deviceId,
    data,
  };
  return await connection.invoke(
    "RequestUpdateLensParameter",
    JSON.stringify(payload)
  );
};

const deviceUpdateSimParameter = async (
  token: string,
  deviceId: string,
  data: {
    simulationEnvironmentType?: string;
  },
  connection: HubConnection
) => {
  const payload = {
    token: injectBearer(token),
    deviceId,
    data,
  };
  return await connection.invoke(
    "RequestUpdateBackgroundParameter",
    JSON.stringify(payload)
  );
};

const deviceFinishLensSim = async (
  token: string,
  deviceId: string,
  connection: HubConnection
) => {
  const payload = {
    token: `Bearer ${token}`,
    deviceId,
  };
  return await connection.send(
    "SendMessageToDevice",
    "DeviceSubmitSectionRequested",
    JSON.stringify(payload)
  );
};

const deviceClearVideoCache = async (
  token: string,
  deviceId: string,
  connection: HubConnection
) => {
  const payload = {
    token: `Bearer ${token}`,
    deviceId,
  };
  return await connection.send(
    "SendMessageToDevice",
    "DeviceClearVideoCacheRequested",
    JSON.stringify(payload)
  );
};

export {
  getPortalHub,
  subscribeToClinic,
  subscribeToDevice,
  unsubscribeFromDevice,
  updateDeviceStage,
  skipExamRequest,
  terminateExamRequest,
  deviceCalibrateLobby,
  deviceEnableControllerBeep,
  deviceDisableControllerBeep,
  deviceEnableWarnings,
  deviceDisableWarnings,
  deviceEnableSubtitles,
  deviceDisableSubtitles,
  deviceUpdateVolume,

  // Lens Sim
  deviceUpdateLensParameter,
  deviceUpdateSimParameter,
  deviceFinishLensSim,
  deviceClearVideoCache,

  // WebRtc
  invokeSendOffer,
  invokeSendIceCandidate,
  invokeNotifyAnswerAccepted,
};
